import React, { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Heading,
  Alert,
  AlertIcon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { getFlowAnalyticsReport } from "../../Services/api";
import * as _ from "lodash";
import DateRangeSelection from "../../components/DateRangeSelection";
import moment from "moment";

const FlowDetailedAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { uuid } = useParams();
  const [flowName, setFlowName] = useState("");

  // Initialize with moment objects
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(15, "days"),
    endDate: moment(),
  });

  // Handle date change from DateRangeSelection component
  // This ensures we always store moment objects, even if the picker passes native Dates
  const onDateChange = (date) => {
    setDateRange({
      startDate: moment(date.startDate), // Convert to moment
      endDate: moment(date.endDate),     // Convert to moment
    });
  };

  const getFlowAnalytics = async (range) => {
    try {
      setLoading(true);
      setError(null);

      // Safely format dates using moment
      const formattedRange = {
        startDate: range.startDate.format("YYYY-MM-DD"),
        endDate: range.endDate.format("YYYY-MM-DD"),
      };

      const res = await getFlowAnalyticsReport(uuid, formattedRange);
      setAnalyticsData(res?.data?.analytics || {});
      setFlowName(res?.data?.ruleName || "Flow Analytics");
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Failed to fetch analytics data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch when date range changes
  useEffect(() => {
    getFlowAnalytics(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Initial fetch on mount
  useEffect(() => {
    getFlowAnalytics(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box textAlign="center" mt={20}>
        <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
        <Text mt={4} fontSize="lg" color="gray.600">
          Loading analytics...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  const brief = analyticsData?.brief || {};
  const details = analyticsData?.details || {};
  const currency = brief.currency || "INR";

  return (
    <Box p={6} maxW="100%" overflowX="auto">
      {/* Header */}
      <Box mb={6}>
        <Heading size="lg" mb={2}>
          {flowName}
        </Heading>
        <Text color="gray.600" fontSize="sm">
          Detailed performance analytics across all channels and templates.
        </Text>
        <Box mt={4}>
          <DateRangeSelection
            dateRange={{
              startDate: dateRange.startDate,
              endDate: dateRange.endDate,
            }}
            onDateChange={onDateChange}
          />
        </Box>
      </Box>

      {/* Summary Stats */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={6} mb={10}>
        <StatBox label="Sent" value={brief.Sent ?? 0} />
        <StatBox label="Delivered" value={brief.Delivered ?? 0} />
        <StatBox label="Read" value={brief.Opened ?? 0} />
        <StatBox label="Clicked" value={brief.Clicked ?? 0} />
        <StatBox label="Orders" value={brief.Orders ?? 0} />
        <StatBox label="Revenue" value={brief.Revenue ?? 0} currency={currency} />
      </SimpleGrid>

      {/* Channel-wise Detailed Tables */}
      {Object.keys(details).length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No detailed analytics available for the selected period.
        </Alert>
      ) : (
        Object.keys(details).map((channelKey) => {
          const channelData = details[channelKey];
          if (!Array.isArray(channelData) || channelData.length === 0) return null;

          const isWhatsApp = channelKey.toLowerCase() === "whatsapp";
          const channelName = _.startCase(channelKey);

          return (
            <Box key={channelKey} mt={10}>
              <Heading size="md" mb={4}>
                {channelName}
              </Heading>

              <TableContainer
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                shadow="sm"
                bg="white"
              >
                <Table variant="simple" size="md">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Template Name</Th>
                      {isWhatsApp ? (
                        <>
                          <Th isNumeric>Sent</Th>
                          <Th isNumeric>Delivered</Th>
                          <Th isNumeric>Read</Th>
                          <Th isNumeric>Clicked</Th>
                          <Th isNumeric>Orders</Th>
                          <Th isNumeric>Revenue</Th>
                        </>
                      ) : (
                        <>
                          <Th isNumeric>Sent</Th>
                          <Th isNumeric>Delivered</Th>
                          <Th isNumeric>Clicks</Th>
                          <Th isNumeric>Unique Clicks</Th>
                          <Th isNumeric>Opened</Th>
                          <Th isNumeric>Bounced</Th>
                          <Th isNumeric>CTR (%)</Th>
                        </>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {channelData.map((item, idx) => {
                      const all = item.all || {};
                      const template = item.template || {};

                      const sent = all.totalSent || 0;
                      const delivered = all.totalDelivered || 0;
                      const read = all.totalReceived || 0;
                      const clicked = all.totalClicked || 0;
                      const uniqueClicked = all.uniqueClicked || 0;
                      const opened = all.totalOpened || 0;
                      const bounced = all.totalBounced || 0;
                      const orders = all.totalOrders || 0;
                      const revenue = all.revenue || 0;
                      const ctr =
                        sent > 0 ? ((clicked / sent) * 100).toFixed(2) : "0.00";

                      return (
                        <Tr key={idx} _hover={{ bg: "gray.50" }}>
                          <Td
                            fontWeight="medium"
                            maxW="300px"
                            isTruncated
                            title={template.name}
                          >
                            {template.name || "Unnamed Template"}
                          </Td>

                          {isWhatsApp ? (
                            <>
                              <Td isNumeric>{sent}</Td>
                              <Td isNumeric>{delivered}</Td>
                              <Td isNumeric>{read}</Td>
                              <Td isNumeric>{clicked}</Td>
                              <Td isNumeric>{orders}</Td>
                              <Td
                                isNumeric
                                fontWeight="semibold"
                                color="green.600"
                              >
                                {revenue > 0
                                  ? `${currency} ${revenue.toLocaleString()}`
                                  : "0"}
                              </Td>
                            </>
                          ) : (
                            <>
                              <Td isNumeric>{sent}</Td>
                              <Td isNumeric>{delivered}</Td>
                              <Td isNumeric>{clicked}</Td>
                              <Td isNumeric>{uniqueClicked}</Td>
                              <Td isNumeric>{opened}</Td>
                              <Td isNumeric>{bounced}</Td>
                              <Td isNumeric fontWeight="medium">
                                {ctr}%
                              </Td>
                            </>
                          )}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          );
        })
      )}
    </Box>
  );
};

// Reusable Stat Box Component
const StatBox = ({ label, value, currency }) => (
  <Stat
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="lg"
    p={5}
    shadow="sm"
    transition="all 0.2s"
    _hover={{ shadow: "md", transform: "translateY(-2px)" }}
  >
    <StatLabel fontSize="sm" color="gray.600">
      {label}
    </StatLabel>
    <StatNumber fontSize="2xl" fontWeight="bold" color="blue.600">
      {currency
        ? `${currency} ${Number(value).toLocaleString()}`
        : Number(value).toLocaleString()}
    </StatNumber>
  </Stat>
);

export default FlowDetailedAnalytics;